import { useMemo } from 'react'
import { differenceInMinutes } from 'date-fns'
import { useTranslations } from 'next-intl'
import { Availability } from '~/components/search-and-results/TimePicker/interface'
import { checkDisabledTime, getDateTime } from '@reward-platform/lift/utils/datetime'
import {
  CarHireLocation,
  getCommonBrandIds,
  getServingOpeningTimes,
  hasNoBrandInCommon,
  hasNoBrandOpen,
  isClosedOnDate,
} from '@reward-platform/ancillaries-locations'
import { useLocalisation } from '~/context/localisation'
import { useFeatureFlag } from '~/context/featureFlag'
import { setTimeToDate } from '~/utils/datetime'
import { CarHireSearchFormSchema } from '../SearchCarHireForm.types'
import { validateTimeLaterThanMinimumTime } from '../helpers/validateTimeLaterThanMinimumTime'
import { MAX_DAYS_RENTAL } from './useCarHireSearchForm'

const getOpeningTimesForValidation = (
  multiBrandEnabled: boolean,
  location: CarHireLocation | undefined,
  servingBrandIds: string[],
  allowAfterHoursDropOff = false
) =>
  getServingOpeningTimes(location, multiBrandEnabled ? servingBrandIds : [], allowAfterHoursDropOff)

export function useCarHireSearchFormValidation() {
  const { locale } = useLocalisation()
  const multiBrandEnabled = useFeatureFlag('car-hire-multi-brand')
  const tValidation = useTranslations('pages.search.car_hire.validation')

  const errorText = {
    missingPickUpLocation: tValidation('missing_pick_up'),
    missingDropOffLocation: tValidation('missing_drop_off'),
    dropOffInCountry: tValidation('drop_off_in_country'),
    pickUpNotPast: tValidation('pick_up_not_past'),
    timeNotAvailable: tValidation('time_not_available'),
    dateNotAvailable: tValidation('date_not_available'),
    minimumRentalPeriod: tValidation('minimum_rental_period'),
    maximumRentalPeriod: tValidation('maximum_rental_period', { maxDays: MAX_DAYS_RENTAL }),
    minimumAvisPreferredMembershipLength: tValidation('minimum_avis_membership_length'),
    noBrandInCommon: tValidation('no_brand_in_common'),
    noBrandOpen: tValidation('no_brand_open'),
  }

  const CarHireSearchFormValidationSchema = CarHireSearchFormSchema.partial()
    .refine(({ pickUpLocation }) => !!pickUpLocation?.id, {
      path: ['pickUpSearchString'],
      message: errorText.missingPickUpLocation,
    })
    .refine(({ differentDropOff, dropOffLocation }) => !differentDropOff || !!dropOffLocation?.id, {
      path: ['dropOffSearchString'],
      message: errorText.missingDropOffLocation,
    })
    .refine(
      ({ pickUpLocation, pickUpDate, differentDropOff, dropOffLocation }) => {
        if (!pickUpLocation?.id || !pickUpDate) {
          return true
        }
        const servingBrandIds = multiBrandEnabled
          ? getCommonBrandIds(pickUpLocation, differentDropOff ? dropOffLocation : undefined)
          : []
        return !isClosedOnDate(
          getOpeningTimesForValidation(multiBrandEnabled, pickUpLocation, servingBrandIds),
          pickUpDate
        )
      },
      {
        path: ['pickUpDate'],
        message: errorText.dateNotAvailable,
      }
    )
    .refine(
      ({ pickUpLocation, differentDropOff, dropOffLocation, dropOffDate }) => {
        const location = differentDropOff ? dropOffLocation : pickUpLocation
        if (!location?.id || !dropOffDate) {
          return true
        }
        const servingBrandIds = multiBrandEnabled
          ? getCommonBrandIds(pickUpLocation, differentDropOff ? dropOffLocation : undefined)
          : []
        return !isClosedOnDate(
          getOpeningTimesForValidation(multiBrandEnabled, location, servingBrandIds, true),
          dropOffDate
        )
      },
      {
        path: ['dropOffDate'],
        message: errorText.dateNotAvailable,
      }
    )
    .refine(
      ({
        pickUpLocation,
        pickUpTime,
        pickUpDate,
        differentDropOff,
        dropOffLocation,
        dropOffDate,
        dropOffTime,
      }) => {
        if (!pickUpLocation?.id) {
          return true
        }

        const servingBrandIds = multiBrandEnabled
          ? getCommonBrandIds(pickUpLocation, differentDropOff ? dropOffLocation : undefined)
          : []
        const openingTimes = getOpeningTimesForValidation(
          multiBrandEnabled,
          pickUpLocation,
          servingBrandIds
        )
        if (pickUpDate && isClosedOnDate(openingTimes, pickUpDate)) {
          return true
        }

        // Let the dedicated noBrandOpen refinement provide the actionable message when the
        // selected time is inside the row's union hours but no common brand is actually open.
        // Otherwise this generic time refinement wins because both errors share pickUpTime.
        if (
          multiBrandEnabled &&
          pickUpDate &&
          pickUpTime &&
          dropOffDate &&
          dropOffTime &&
          hasNoBrandOpen({
            pickUpLocation,
            dropOffLocation: differentDropOff ? dropOffLocation : undefined,
            pickUpDateTime: getDateTime(pickUpTime, pickUpDate),
            dropOffDateTime: getDateTime(dropOffTime, dropOffDate),
          })
        ) {
          return true
        }

        return !checkDisabledTime(
          locale,
          pickUpTime ?? '',
          pickUpDate,
          openingTimes as Availability[]
        )
      },
      {
        path: ['pickUpTime'],
        message: errorText.timeNotAvailable,
      }
    )
    .refine(
      ({ pickUpTime, pickUpDate, dropOffTime, dropOffDate }) => {
        const pickUpDateTime = setTimeToDate(pickUpDate!.toISOString(), pickUpTime)
        const dropOffDateTime = setTimeToDate(dropOffDate!.toISOString(), dropOffTime)
        return differenceInMinutes(dropOffDateTime, pickUpDateTime) / 60 / 24 <= MAX_DAYS_RENTAL
      },
      {
        path: ['dropOffTime'],
        message: errorText.maximumRentalPeriod,
      }
    )
    .refine(
      ({ pickUpLocation, pickUpTime, pickUpDate }) =>
        !pickUpLocation?.id || !pickUpTime || !pickUpDate
          ? true
          : validateTimeLaterThanMinimumTime(pickUpTime, pickUpDate),
      {
        path: ['pickUpTime'],
        message: errorText.pickUpNotPast,
      }
    )
    .refine(
      ({ pickUpLocation, dropOffTime, dropOffDate, pickUpTime, pickUpDate }) =>
        !pickUpLocation?.id
          ? true
          : dropOffDate &&
            validateTimeLaterThanMinimumTime(
              dropOffTime ?? '',
              dropOffDate,
              pickUpTime,
              pickUpDate
            ),
      {
        path: ['dropOffTime'],
        message: errorText.minimumRentalPeriod,
      }
    )
    .refine(
      ({
        pickUpLocation,
        dropOffTime,
        dropOffDate,
        differentDropOff,
        dropOffLocation,
        pickUpTime,
        pickUpDate,
      }) => {
        if (!pickUpLocation?.id) {
          return true
        }
        /**
         * If the drop off location is the same as the pick up location, we need to check the opening times of the pick up location. If the dropOffDate is the same as the pickUpDate, we need to check that the dropOffTime is after the pickUpTime.
         */
        const servingBrandIds = multiBrandEnabled
          ? getCommonBrandIds(pickUpLocation, differentDropOff ? dropOffLocation : undefined)
          : []
        const dropOffLocationForValidation = differentDropOff ? dropOffLocation : pickUpLocation
        const dateOpeningTimes = getOpeningTimesForValidation(
          multiBrandEnabled,
          dropOffLocationForValidation,
          servingBrandIds,
          true
        )
        if (dropOffDate && isClosedOnDate(dateOpeningTimes, dropOffDate)) {
          return true
        }
        const openingTimes = differentDropOff
          ? getOpeningTimesForValidation(multiBrandEnabled, dropOffLocation, servingBrandIds, true)
          : getOpeningTimesForValidation(multiBrandEnabled, pickUpLocation, servingBrandIds, true)
        const minDateTime =
          pickUpTime && pickUpDate ? getDateTime(pickUpTime, pickUpDate) : undefined
        return !checkDisabledTime(locale, dropOffTime ?? '', dropOffDate, openingTimes, minDateTime)
      },
      {
        path: ['dropOffTime'],
        message: errorText.timeNotAvailable,
      }
    )
    .refine(
      ({ pickUpLocation, differentDropOff, dropOffLocation }) =>
        !differentDropOff ||
        (pickUpLocation?.id &&
          dropOffLocation?.id &&
          pickUpLocation?.countryCode &&
          dropOffLocation?.countryCode &&
          pickUpLocation?.countryCode === dropOffLocation?.countryCode),
      {
        path: ['dropOffSearchString'],
        message: errorText.dropOffInCountry,
      }
    )
    .refine(
      ({ differentDropOff, pickUpLocation, dropOffLocation }) =>
        !multiBrandEnabled ||
        !differentDropOff ||
        !hasNoBrandInCommon(pickUpLocation, dropOffLocation),
      {
        path: ['dropOffSearchString'],
        message: errorText.noBrandInCommon,
      }
    )
    // Catch the trip no brand can serve at the chosen times before a search is sent that cannot
    // return anything. The time pickers already exclude those times, so this only fires when the
    // times were restored from a URL or the stored opening hours changed under the customer.
    .refine(
      ({
        pickUpLocation,
        dropOffLocation,
        differentDropOff,
        pickUpDate,
        pickUpTime,
        dropOffDate,
        dropOffTime,
      }) => {
        if (!pickUpLocation?.id || !pickUpDate || !pickUpTime || !dropOffDate || !dropOffTime) {
          return true
        }
        const servingBrandIds = multiBrandEnabled
          ? getCommonBrandIds(pickUpLocation, differentDropOff ? dropOffLocation : undefined)
          : []
        const returnLocation = differentDropOff ? dropOffLocation : pickUpLocation
        if (
          isClosedOnDate(
            getOpeningTimesForValidation(multiBrandEnabled, pickUpLocation, servingBrandIds),
            pickUpDate
          ) ||
          isClosedOnDate(
            getOpeningTimesForValidation(multiBrandEnabled, returnLocation, servingBrandIds, true),
            dropOffDate
          )
        ) {
          return true
        }
        return (
          !multiBrandEnabled ||
          !hasNoBrandOpen({
            pickUpLocation,
            dropOffLocation: differentDropOff ? dropOffLocation : undefined,
            pickUpDateTime: getDateTime(pickUpTime, pickUpDate),
            dropOffDateTime: getDateTime(dropOffTime, dropOffDate),
          })
        )
      },
      {
        path: ['pickUpTime'],
        message: errorText.noBrandOpen,
      }
    )
    .refine(
      ({ membershipCode, isAvisPreferred }) => {
        const regexp = /^[A-Za-z0-9]{1,6}$/

        if (membershipCode && isAvisPreferred) {
          return regexp.test(membershipCode.trim().toUpperCase())
        }
        return true
      },
      {
        path: ['membershipCode'],
        message: errorText.minimumAvisPreferredMembershipLength,
      }
    )

  return useMemo(() => CarHireSearchFormValidationSchema, [CarHireSearchFormValidationSchema])
}
